import { StoreKey } from "lib/Intl";
import getConfig from "next/config";
import {
  createContext,
  type ReactNode,
  useMemo,
  useReducer,
  useRef,
} from "react";
import { useCart } from "src/hooks/useCart";
import { useCreateCart } from "src/hooks/useCreateCart";
import { CartInfo } from "src/types/cartInfo";
import { createReducer, type Dispatch } from "src/utils/create-reducer";
import { getCartInfo, getCartItemsCount } from "src/utils/ct/cart-helper";
import { storeConfigs } from "src/utils/storeConfig";

export type CartState = {
  id: string;
  version: number;
  totalItems: number;
};

const initialState: CartState = {
  id: "",
  version: 0,
  totalItems: 0,
};

export type CartActions =
  | { type: "SET_CART_STATE"; payload: CartState }
  | {
      type: "SET_CART_INFO";
      payload: CartInfo;
    }
  | {
      type: "SET_CART_VERSION";
      payload: number;
    }
  | {
      type: "SET_TOTAL_ITEMS";
      payload: number;
    };

export type UseCartContextReturn = {
  cartState: CartState;
  cartContextDispatch: Dispatch<CartActions>;
};

export const CartContext = createContext<CartState | undefined>(undefined);

export const CartContextDispatch = createContext<
  Dispatch<CartActions> | undefined
>(undefined);

export const cartReducer = createReducer<CartState, CartActions>({
  SET_CART_STATE: (state, payload) => ({
    ...payload,
  }),
  SET_CART_INFO: (state, payload) => ({
    ...state,
    ...payload,
  }),
  SET_CART_VERSION: (state, payload) => ({
    ...state,
    version: payload,
  }),
  SET_TOTAL_ITEMS: (state, payload) => ({
    ...state,
    totalItems: payload,
  }),
});

const { publicRuntimeConfig } = getConfig();

export function CartContextProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [cartState, cartContextDispatch] = useReducer(
    cartReducer,
    initialState
  );

  const createCarteRef = useRef(true);
  const { createCart } = useCreateCart({
    refetchQueries: ["cart"],
    onCompleted: (cartData) => {
      cartContextDispatch({
        type: "SET_CART_STATE",
        payload: {
          ...getCartInfo(cartData?.createMyCart),
          totalItems: getCartItemsCount(
            cartData?.createMyCart?.lineItems || []
          ),
        },
      });
    },
  });

  useCart({
    onCompleted: (cartData) => {
      if (createCarteRef.current && !cartData.inStore?.me?.activeCart) {
        // Make sure a cart is created when the user doesn't have one yet
        void createCart({
          currency:
            storeConfigs[
              (publicRuntimeConfig.STORE_KEY as StoreKey) || "OT-COM"
            ].currencyCode,
        });

        createCarteRef.current = false;
        // Cart query is refetch once create cart is completed
        return;
      }

      cartContextDispatch({
        type: "SET_CART_STATE",
        payload: {
          ...getCartInfo(cartData.inStore?.me?.activeCart),
          totalItems: getCartItemsCount(
            cartData.inStore?.me?.activeCart?.lineItems || []
          ),
        },
      });
    },
  });

  const cartContextValue = useMemo(() => cartState, [cartState]);

  return (
    <CartContext.Provider value={cartContextValue}>
      <CartContextDispatch.Provider value={cartContextDispatch}>
        {children}
      </CartContextDispatch.Provider>
    </CartContext.Provider>
  );
}
